import { appSyncService } from "../../services/appsync/service";
import type { ChatEvent } from "../../services/appsync/types";

const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

const STEP_DELAY_MS = 45;
const TOOL_DELAY_MS = 35;
const TEXT_CHUNK_DELAY_MS = 18;

const chunkText = (text: string, maxChunkLength = 96): string[] => {
  const words = text.split(/\s+/).filter(Boolean);
  const chunks: string[] = [];
  let current = "";

  for (const word of words) {
    const candidate = current ? `${current} ${word}` : word;
    if (candidate.length > maxChunkLength && current) {
      chunks.push(current);
      current = word;
    } else {
      current = candidate;
    }
  }

  if (current) {
    chunks.push(current);
  }

  return chunks;
};

const buildThreadSequence = (threadId: string, sequence: number) =>
  `${threadId}#${String(sequence).padStart(12, "0")}`;

const buildFakeAssistantText = (question: string, runLabel: string) =>
  `This is the local fallback Nucleus runtime stream for ${runLabel}. ` +
  `The normal path now starts in backendgateway, which publishes the fake event stream through AppSync. ` +
  `The real runtime will later load the selected run, call shipment-planning tools like ` +
  `get_run_metadata and query_ui_supply_walk, and stream AG-UI-style tool traces back through AppSync. ` +
  `Current prompt: ${question}`;

interface FakeTurnContext {
  organizationId: string;
  threadId: string;
  question: string;
  runLabel: string;
  runSettingsJson: string;
}

interface FakeTurnOptions {
  mode?: "appsync" | "local";
  nextSequence: () => number;
  onEventAppended?: (event: ChatEvent) => void;
}

const appendEvent = async (
  context: FakeTurnContext,
  turnId: string,
  eventType: string,
  payload: Record<string, unknown>,
  options: FakeTurnOptions
) => {
  const sequence = options.nextSequence();
  const localEvent: ChatEvent = {
    organizationId: context.organizationId,
    threadId: context.threadId,
    turnId,
    eventId: crypto.randomUUID(),
    sequence,
    eventType,
    payload: JSON.stringify(payload),
    createdAt: new Date().toISOString(),
  };

  const event =
    options.mode === "local"
      ? localEvent
      : await appSyncService.appendChatEvent({
          organizationId: context.organizationId,
          threadId: context.threadId,
          turnId,
          eventId: localEvent.eventId,
          threadSequence: buildThreadSequence(context.threadId, sequence),
          sequence,
          eventType,
          payload: localEvent.payload,
          createdAt: localEvent.createdAt,
        });

  if (options.onEventAppended) {
    options.onEventAppended(event);
  }

  return event;
};

export const runFakeChatTurn = async (
  context: FakeTurnContext,
  options: FakeTurnOptions
) => {
  const turnId = crypto.randomUUID();
  const assistantText = buildFakeAssistantText(context.question, context.runLabel);

  await appendEvent(context, turnId, "USER_MESSAGE", { text: context.question }, options);
  await sleep(STEP_DELAY_MS);

  await appendEvent(context, turnId, "RUN_STARTED", { mode: "fake-runtime" }, options);
  await sleep(STEP_DELAY_MS);

  await appendEvent(
    context,
    turnId,
    "ACTIVITY_SNAPSHOT",
    {
      label: "LOADING_RUN_CONTEXT",
      detail: "Loading selected run and thread context",
    },
    options
  );
  await sleep(STEP_DELAY_MS);

  await appendEvent(
    context,
    turnId,
    "REASONING_MESSAGE_START",
    {
      messageId: crypto.randomUUID(),
      role: "reasoning",
    },
    options
  );
  await appendEvent(
    context,
    turnId,
    "REASONING_MESSAGE_CONTENT",
    {
      delta: "I’m loading the selected run before I inspect shipment-planning data.",
    },
    options
  );
  await appendEvent(
    context,
    turnId,
    "REASONING_MESSAGE_END",
    {},
    options
  );
  await sleep(STEP_DELAY_MS);

  await appendEvent(
    context,
    turnId,
    "STEP_STARTED",
    {
      stepId: "load_run_context",
      label: "Load run context",
    },
    options
  );
  await sleep(STEP_DELAY_MS);

  await appendEvent(
    context,
    turnId,
    "STEP_FINISHED",
    {
      stepId: "load_run_context",
      label: "Load run context",
      summary: "Selected run context loaded",
    },
    options
  );
  await sleep(STEP_DELAY_MS);

  await appendEvent(
    context,
    turnId,
    "TOOL_CALL_START",
    {
      toolName: "get_run_metadata",
      displayLabel: "Loading run context",
    },
    options
  );
  await sleep(TOOL_DELAY_MS);

  await appendEvent(
    context,
    turnId,
    "TOOL_CALL_ARGS",
    {
      toolName: "get_run_metadata",
      displayLabel: "Loading run context",
      argsSummary: {
        runSettings: context.runSettingsJson,
      },
    },
    options
  );
  await sleep(TOOL_DELAY_MS);

  await appendEvent(
    context,
    turnId,
    "TOOL_CALL_END",
    {
      toolName: "get_run_metadata",
      displayLabel: "Loading run context",
      resultSummary: {
        status: "Fake metadata lookup completed",
      },
    },
    options
  );
  await sleep(TOOL_DELAY_MS);

  await appendEvent(
    context,
    turnId,
    "TOOL_CALL_START",
    {
      toolName: "query_ui_supply_walk",
      displayLabel: "Inspecting supply walk",
    },
    options
  );
  await sleep(TOOL_DELAY_MS);

  await appendEvent(
    context,
    turnId,
    "TOOL_CALL_END",
    {
      toolName: "query_ui_supply_walk",
      displayLabel: "Inspecting supply walk",
      resultSummary: {
        status: "Fake shipment-risk lookup completed",
      },
    },
    options
  );
  await sleep(TOOL_DELAY_MS);

  const messageId = crypto.randomUUID();
  await appendEvent(context, turnId, "TEXT_MESSAGE_START", { messageId }, options);

  for (const delta of chunkText(assistantText)) {
    await sleep(TEXT_CHUNK_DELAY_MS);
    await appendEvent(context, turnId, "TEXT_MESSAGE_CONTENT", { messageId, delta }, options);
  }

  await sleep(TEXT_CHUNK_DELAY_MS);
  await appendEvent(context, turnId, "TEXT_MESSAGE_END", { messageId }, options);
  await sleep(TEXT_CHUNK_DELAY_MS);
  await appendEvent(context, turnId, "RUN_FINISHED", { mode: "fake-runtime" }, options);
};
